/**
 * Утилиты для работы с авторизацией
 */
import { getCookie, setCookie, deleteCookie } from './cookieUtils';
import { clearAllCache } from './cacheUtils';
import axios from 'axios';
import { BASE_URL } from './constants';
import i18n from '../i18n/i18n';

/**
 * Проверяет, авторизован ли пользователь
 * @returns {boolean} true, если пользователь авторизован
 */
export const isAuthenticated = (): boolean => {
    const accessToken = getCookie('access_token') || localStorage.getItem('jwt_token');
    if (!accessToken) return false;

    const expiresAt = getCookie('token_expires_at');
    // Если есть время истечения в cookie, проверяем его
    if (expiresAt) {
        return parseInt(expiresAt, 10) > Date.now();
    }

    // Если времени нет (например, токен только в localStorage),
    // считаем, что пользователь может быть авторизован, и позволяем серверу провести валидацию.
    return true;
};

/**
 * Сохраняет токены в куки
 * @param {string} accessToken - Токен доступа
 * @param {string} refreshToken - Токен обновления
 * @param {number} expiresIn - Срок действия в секундах
 */
export const saveTokens = (
    accessToken: string,
    refreshToken: string,
    expiresIn: number = 3600
): void => {
    // Сохраняем access token в localStorage
    localStorage.setItem('jwt_token', accessToken);

    // Сохраняем access token в cookie
    setCookie('access_token', accessToken, expiresIn / 86400); // в днях

    // Сохраняем refresh token на более длительный срок
    setCookie('refresh_token', refreshToken, 30);

    // Сохраняем время истечения
    const expiresAt = Date.now() + (expiresIn * 1000);
    setCookie('token_expires_at', expiresAt.toString(), expiresIn / 86400);

    // Сообщаем о смене токена
    window.dispatchEvent(new Event('token:updated'));
};

/**
 * Очищает все токены и связанные данные авторизации
 */
export const clearTokens = async (): Promise<void> => {
    deleteCookie('access_token');
    deleteCookie('refresh_token');
    deleteCookie('token_expires_at');
    deleteCookie('language'); // Удаляем также cookie языка при выходе
    deleteCookie('i18next'); // Удаляем также cookie i18next при выходе
    localStorage.removeItem('jwt_token'); // Удаляем токен из localStorage
    localStorage.removeItem('last_login_time');
    localStorage.removeItem('i18nextLng'); // Удаляем также язык из localStorage

    // Очищаем весь кеш приложения
    await clearAllCache();
};

/**
 * Проверяет, требуется ли обновление токена
 * @returns {boolean} true, если токен нужно обновить
 */
export const shouldRefreshToken = (): boolean => {
    const expiresAt = getCookie('token_expires_at');
    if (!expiresAt) return false;

    // Принудительно обновляем токен всегда
    return true;
};

/**
 * Авторизация пользователя
 * @param {string} email - Email пользователя
 * @param {string} password - Пароль пользователя
 * @returns {Promise<any>} Результат авторизации
 */
export const login = async (email: string, password: string): Promise<any> => {
    try {
        // Определяем язык на основе email
        let detectedLanguage = 'ru';

        // Проверка на наличие "it" в email для итальянского языка
        if (email.toLowerCase().includes('it')) {
            detectedLanguage = 'it';
        } else {
            // Проверка на формат _XX@ (например, user_it@example.com)
            const languageMatch = email.match(/_([a-z]{2})@/i);
            if (languageMatch && languageMatch[1] && ['ru', 'it'].includes(languageMatch[1].toLowerCase())) {
                detectedLanguage = languageMatch[1].toLowerCase();
            }
        }

        // Устанавливаем определенный язык
        i18n.changeLanguage(detectedLanguage);
        setCookie('language', detectedLanguage, 30);
        setCookie('i18next', detectedLanguage, 30); // Устанавливаем куки i18next
        localStorage.setItem('i18nextLng', detectedLanguage);

        // Добавляем язык в запрос авторизации
        const response = await axios.post(`${BASE_URL}/auth/admin/token`,
            { email, password });

        if (response.data?.access_token) {
            saveTokens(
                response.data.access_token,
                response.data.refresh_token,
                response.data.expires_in || 3600
            );
            return response.data;
        }
        return null;
    } catch (error) {
        console.error(i18n.t('errors.loginError'), error);
        throw error;
    }
};

/**
 * Обновляет токен авторизации
 * @returns {Promise<boolean>} Результат обновления токена
 */
export const refreshToken = async (): Promise<boolean> => {
    const refreshTokenValue = getCookie('refresh_token');
    if (!refreshTokenValue) {
        return false;
    }

    try {
        const response = await axios.post(`${BASE_URL}/auth/refresh`,
            { refresh_token: refreshTokenValue });

        if (response.data?.access_token) {
            saveTokens(
                response.data.access_token,
                response.data.refresh_token || refreshTokenValue,
                response.data.expires_in || 3600
            );
            return true;
        }
        return false;
    } catch (error: any) {
        // Если refresh token истек или недействителен, очищаем токены
        if (error.response?.status === 401 || error.response?.status === 400) {
            await clearTokens();
        }

        return false;
    }
};

/**
 * Выход пользователя
 * @returns {Promise<void>}
 */
export const logout = async (): Promise<void> => {
    const refreshTokenValue = getCookie('refresh_token');
    if (refreshTokenValue) {
        try {
            await axios.post(`${BASE_URL}/auth/logout`,
                { refresh_token: refreshTokenValue });
        } catch (error) {
            console.error(i18n.t('errors.logoutError'), error);
        }
    }
    await clearTokens();
};

/**
 * Проверяет аутентификацию и получает данные пользователя
 * @returns {Promise<{success: boolean, user?: any, error?: string}>}
 */
export const checkUserAuth = async (): Promise<{ success: boolean, user?: any, error?: string }> => {
    const token = getCookie('access_token') || localStorage.getItem('jwt_token');

    if (!token) {
        return { success: false, error: 'Unauthorized' };
    }

    try {
        const response = await axios.get(`${BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data) {
            return { success: true, user: response.data };
        }
        return { success: false, error: 'Failed to fetch user data' };
    } catch (error: any) {
        console.error('Auth check error:', error);
        if (error.response?.status === 401) {
            await clearTokens(); // Очищаем невалидные токены
        }
        return { success: false, error: error.response?.data?.detail || 'Server error' };
    }
};

/**
 * Перенаправляет на страницу логина
 */
export const redirectToLogin = (): void => {
    window.location.href = '/login';
}; 